/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Globe, PackageSearch, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const empty = {
  name: '', stripe_account_id: '', url: '', description: '',
  psp: 'stripe', viva_account_id: '', catalog_url: '', catalog_api_key: '',
};

export default function Vitrines() {
  const [vitrines, setVitrines] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [vivaAccounts, setVivaAccounts] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Auto-map (scrape) state
  const [importOpen, setImportOpen] = useState(false);
  const [importVitrine, setImportVitrine] = useState(null);
  const [importStoreId, setImportStoreId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    const [v, sa, va, st] = await Promise.all([
      api.get('/vitrines'), api.get('/stripe-accounts'), api.get('/viva-accounts'), api.get('/stores'),
    ]);
    setVitrines(v.data);
    setAccounts(sa.data);
    setVivaAccounts(va.data);
    setStores(st.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, stripe_account_id: accounts[0]?.id || '' });
    setOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name, stripe_account_id: v.stripe_account_id || '', url: v.url || '', description: v.description || '',
      psp: v.psp || 'stripe', viva_account_id: v.viva_account_id || '',
      catalog_url: v.catalog_url || '', catalog_api_key: '', has_catalog_key: !!v.has_catalog_key,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    if (form.psp === 'stripe' && !form.stripe_account_id) { toast.error('Compte Stripe requis'); return; }
    if (form.psp === 'viva' && !form.viva_account_id) { toast.error('Compte Viva Wallet requis'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/vitrines/${editing.id}`, form);
        toast.success('Vitrine mise à jour');
      } else {
        await api.post('/vitrines', form);
        toast.success('Vitrine créée');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Supprimer ce site vitrine ? (impossible si des boutiques sont liées)')) return;
    try {
      await api.delete(`/vitrines/${id}`);
      toast.success('Vitrine supprimée');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const openImport = (v) => {
    setImportVitrine(v);
    setImportResult(null);
    // Pre-select the single store using this vitrine, if any.
    const linked = stores.filter((s) => s.vitrine_id === v.id);
    setImportStoreId(linked.length === 1 ? linked[0].id : '');
    setImportOpen(true);
  };

  const runImport = async () => {
    if (!importVitrine) return;
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post(`/vitrines/${importVitrine.id}/import-products`, {
        store_id: importStoreId || '',
      });
      setImportResult(data);
      toast.success(`${data.created + data.updated} produit(s) mappé(s) (${data.detected} détecté(s))`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import impossible');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline mb-2">— Infrastructure</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Sites Vitrines</h1>
          <p className="text-zinc-600 mt-2">Chaque vitrine est rattachée à un compte Stripe et peut héberger plusieurs boutiques Shopify.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="admin-add-vitrine-button" className="btn-primary rounded-md">
              <Plus className="h-4 w-4 mr-2" /> Ajouter une vitrine
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? 'Modifier la vitrine' : 'Nouveau site vitrine'}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                Une vitrine relie un compte Stripe à plusieurs boutiques Shopify.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">NOM</Label>
                <Input
                  data-testid="admin-vitrine-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="Lumeacare"
                />
              </div>
              <div>
                <Label className="text-xs">PRESTATAIRE DE PAIEMENT (PSP)</Label>
                <select
                  data-testid="admin-vitrine-psp-select"
                  value={form.psp}
                  onChange={(e) => setForm({ ...form, psp: e.target.value })}
                  className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                >
                  <option value="stripe">Stripe (paiement intégré)</option>
                  <option value="viva">Viva Wallet (carte intégrée)</option>
                </select>
              </div>

              {form.psp === 'stripe' && (
                <div>
                  <Label className="text-xs">COMPTE STRIPE</Label>
                  <select
                    data-testid="admin-vitrine-stripe-select"
                    value={form.stripe_account_id}
                    onChange={(e) => setForm({ ...form, stripe_account_id: e.target.value })}
                    className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                  >
                    <option value="">— Sélectionner —</option>
                    {accounts.map((a) => {
                      const label = `${a.name} (${a.public_key_masked})`;
                      return <option key={a.id} value={a.id}>{label}</option>;
                    })}
                  </select>
                </div>
              )}

              {form.psp === 'viva' && (
                <div>
                  <Label className="text-xs">COMPTE VIVA WALLET</Label>
                  <select
                    data-testid="admin-vitrine-viva-account-select"
                    value={form.viva_account_id}
                    onChange={(e) => setForm({ ...form, viva_account_id: e.target.value })}
                    className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                  >
                    <option value="">— Sélectionner —</option>
                    {vivaAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.environment === 'prod' ? 'Live' : 'Demo'}){a.configured ? '' : ' — incomplet'}
                      </option>
                    ))}
                  </select>
                  {vivaAccounts.length === 0 && (
                    <p className="text-[11px] text-amber-700 mt-1.5">
                      Aucun compte Viva. Créez-en un dans l'onglet <strong>Viva Wallet</strong>.
                    </p>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs">URL DU SITE VITRINE</Label>
                <Input
                  data-testid="admin-vitrine-url-input"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs"
                  placeholder="https://lumeacare.site"
                />
              </div>
              <div>
                <Label className="text-xs">DESCRIPTION (optionnel)</Label>
                <Input
                  data-testid="admin-vitrine-desc-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="Niche détailing auto / cosmétique / etc."
                />
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 bg-zinc-50/60 space-y-3">
                <p className="text-[11px] text-zinc-500">Catalogue propre à cette vitrine (produits + prix). Utilisé lors de la sync de cette vitrine pour la rotation.</p>
                <div>
                  <Label className="text-xs">URL DU CATALOGUE (optionnel)</Label>
                  <Input
                    data-testid="admin-vitrine-catalog-url-input"
                    value={form.catalog_url}
                    onChange={(e) => setForm({ ...form, catalog_url: e.target.value })}
                    className="mt-1.5 h-10 rounded-md font-mono text-xs"
                    placeholder="https://lumeacare.site/products.json"
                  />
                </div>
                <div>
                  <Label className="text-xs">CLÉ API CATALOGUE (optionnel) {editing && '— laisser vide pour ne pas changer'}</Label>
                  <Input
                    data-testid="admin-vitrine-catalog-key-input"
                    value={form.catalog_api_key}
                    onChange={(e) => setForm({ ...form, catalog_api_key: e.target.value })}
                    className="mt-1.5 h-10 rounded-md font-mono text-xs"
                    placeholder={editing && form.has_catalog_key ? '•••••••• (déjà enregistrée)' : 'clé optionnelle'}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Annuler</Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="admin-save-vitrine-button"
                className="btn-primary rounded-md"
              >
                {editing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {accounts.length === 0 && (
        <Card className="card-flat p-6 mb-6 border-amber-300 bg-amber-50">
          <p className="text-sm text-amber-800">
            ℹ️ Aucun <strong>compte Stripe</strong> configuré. Les vitrines <strong>Stripe</strong> en ont besoin — les vitrines <strong>Viva Wallet</strong> fonctionnent sans.
          </p>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {vitrines.length === 0 ? (
          <Card className="card-flat p-10 col-span-full text-center">
            <Globe className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
            <h3 className="font-display font-bold mb-1">Aucune vitrine</h3>
            <p className="text-zinc-500 text-sm">Créez votre premier site vitrine pour relier vos boutiques Shopify à un Stripe.</p>
          </Card>
        ) : vitrines.map((v) => {
          const sa = accounts.find((a) => a.id === v.stripe_account_id);
          const va = vivaAccounts.find((a) => a.id === v.viva_account_id);
          return (
            <Card key={v.id} className="card-flat p-6" data-testid={`vitrine-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold tracking-tight mb-1 truncate">{v.name}</div>
                  {v.url && (
                    <a href={v.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-600 font-mono truncate block hover:underline">
                      {v.url}
                    </a>
                  )}
                  {v.description && <p className="text-xs text-zinc-500 mt-1">{v.description}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                    {v.psp === 'viva' ? (
                      <span className={`px-2 py-0.5 rounded-md border font-mono ${va ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                        {va ? `Viva · ${va.name} (${va.environment === 'prod' ? 'Live' : 'Demo'})` : 'Viva (compte manquant)'}
                      </span>
                    ) : sa ? (
                      <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 font-mono">
                        Stripe · {sa.name}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 font-mono">Stripe</span>
                    )}
                    <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 font-mono">
                      {v.stores_count || 0} boutique{v.stores_count > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openImport(v)}
                    data-testid={`admin-import-vitrine-${v.id}`}
                    title="Mapper automatiquement les produits de la vitrine"
                    className="rounded-md text-zinc-700 hover:text-black"
                  >
                    <PackageSearch className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(v)} data-testid={`admin-edit-vitrine-${v.id}`} className="rounded-md">
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(v.id)}
                    data-testid={`admin-delete-vitrine-${v.id}`}
                    className="rounded-md text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Auto-map (scrape) dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Mapper les produits de la vitrine</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Lecture sécurisée du catalogue de <strong>{importVitrine?.name}</strong> (prix côté serveur — jamais depuis le navigateur). Le snippet universel n'enverra que l'identifiant du produit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 text-xs text-zinc-600 font-mono break-all">
              {importVitrine?.url || '⚠️ Aucune URL sur cette vitrine — ajoutez-la via « Modifier ».'}
            </div>
            <div>
              <Label className="text-xs">BOUTIQUE CIBLE</Label>
              <select
                data-testid="admin-import-store-select"
                value={importStoreId}
                onChange={(e) => setImportStoreId(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
              >
                <option value="">— Détecter automatiquement —</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.vitrine_id === importVitrine?.id ? ' (vitrine active)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Les produits scrappés sont rattachés à cette boutique (branding + compte Stripe/Viva via sa vitrine active).
              </p>
            </div>
            {importResult && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="admin-import-result">
                <p className="font-semibold text-emerald-800">
                  {importResult.detected} détecté(s) · {importResult.created} créé(s) · {importResult.updated} mis à jour → {importResult.store_name}
                </p>
                {importResult.sample?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-emerald-900/80 font-mono">
                    {importResult.sample.map((p) => (
                      <li key={p.slug} className="flex justify-between gap-3">
                        <span className="truncate">{p.name}</span>
                        <span className="shrink-0">{p.price.toFixed(2)} €</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setImportOpen(false)}>Fermer</Button>
            <Button
              onClick={runImport}
              disabled={importing || !importVitrine?.url}
              data-testid="admin-run-import-button"
              className="btn-primary rounded-md"
            >
              {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lecture…</> : <><PackageSearch className="h-4 w-4 mr-2" /> Mapper les produits</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
